<template>
  <div v-if="initialised" class="app">
    <core-header fixed>
      <b-link class="navbar-brand" to="/jobs" />
      <button class="d-md-down-none navbar-toggler" @click.prevent="toggleSidebar">
        <span class="navbar-toggler-icon"></span>
      </button>

      <core-breadcrumb :list="list" />

      <b-navbar-nav class="ml-auto mr-4">
        <b-nav-item class="d-md-down-none mr-4">
          <div @click="toggleTheme">
            <span style="position: relative; top: -7px; left: -6px">Dark Mode</span>
            <core-switch
              v-model="isDarkTheme"
              variant="pill"
              color="secondary"
              label
              v-bind="themeText"
              :default-checked="false"
              :checked="false"
            />
          </div>
        </b-nav-item>
        <b-nav-item v-if="false" class="d-md-down-none">
          <achievement />
        </b-nav-item>
        <b-nav-item v-if="$can('read', 'header-accepted-quotes')" class="d-md-down-none">
          <accepted-quotes />
        </b-nav-item>
        <b-nav-item v-if="$can('read', 'header-reminders')" class="d-md-down-none">
          <reminders />
        </b-nav-item>
        <b-nav-item class="d-md-down-none">
          <bolt-app-switcher :urls="appUrls" />
        </b-nav-item>
      </b-navbar-nav>
    </core-header>

    <div class="app-body">
      <keep-alive>
        <core-sidebar fixed>
          <template v-if="$store.state.menu.initialised">
            <core-sidebar-nav :nav-items="$store.state.menu.items" />
            <core-sidebar-minimizer />
          </template>
          <template v-else>
            <div></div>
          </template>
        </core-sidebar>
      </keep-alive>

      <main class="main">
        <div class="container-fluid">
          <router-view />
        </div>
      </main>

      <core-aside v-if="false" fixed>
        <div><!-- Nothing yet --></div>
      </core-aside>
    </div>

    <core-footer>
      <footer-stats />
    </core-footer>

    <b-modal
      id="new-reminder"
      ref="newReminderModalRef"
      size="lg"
      :hide-footer="true"
      title="Add a New Reminder"
    >
      <reminder-form
        in-modal="create"
        :job-id="$route.name === 'jobs.edit' ? $route.params.id : null"
      />
    </b-modal>

    <b-modal id="achievement-toast" centered size="sm" :hide-header="true" :hide-footer="true">
      <div v-if="$lodash.has($store.state.achievement, 'currentTier.title')" class="text-center">
        Congrats on achieving
        {{ $store.state.achievement.currentTier.title }} medal!
      </div>
    </b-modal>

    <vue-progress-bar />
  </div>
</template>

<script>
import {
  Aside as CoreAside,
  Breadcrumb as CoreBreadcrumb,
  Footer as CoreFooter,
  Header as CoreHeader,
  Sidebar as CoreSidebar,
  SidebarMinimizer as CoreSidebarMinimizer,
  SidebarNav as CoreSidebarNav,
} from "@coreui/vue";
import externals from "../constants/externals";
import AcceptedQuotes from "../components/header/AcceptedQuotes.vue";
import FooterStats from "../components/FooterStats.vue";
import ReminderForm from "../views/reminders/Edit.vue";
import Reminders from "../components/header/Reminders.vue";
import Achievement from "../components/header/Achievement.vue";
import BoltAppSwitcher from "../components/BoltAppSwitcher.vue";
import EventBus from "../imports/event-bus";

export default {
  name: "Admin",

  components: {
    CoreAside,
    CoreBreadcrumb,
    CoreFooter,
    CoreHeader,
    CoreSidebar,
    CoreSidebarMinimizer,
    CoreSidebarNav,

    Achievement,
    AcceptedQuotes,
    FooterStats,
    ReminderForm,
    Reminders,

    BoltAppSwitcher,
  },

  data() {
    return {
      initialised: false,
      isDarkTheme: false,
      appUrls: {},

      themeText: {
        dataOn: "ON",
        dataOff: "OFF",
      },
    };
  },

  computed: {
    name() {
      return this.$route.name;
    },

    list() {
      return this.$route.matched
        .filter((route) => route.name || route.meta.label)
        .map((route) => {
          const formatted = route;

          formatted.name = formatted.name.replace(".", " ").replace("-", " ");

          return formatted;
        });
    },
  },

  mounted() {
    // Initialise theme
    if (localStorage.isDarkTheme) {
      this.isDarkTheme = localStorage.isDarkTheme === "true";
    }
    this.initTheme();

    this.createGoogleMaps().then(() => {
      this.$store.state.address.google = window.google;
    });

    this.$Progress.finish();

    this.$auth.load().then(() => {
      this.$store.commit("menu/initialise", {
        userId: this.$auth.user() ? this.$auth.user().id : null,
        menuFilter: (item) => this.$can("navigate", item.name.toLowerCase()),
      });

      this.initAppUrls();

      this.initialised = true;
    });

    EventBus.$on("new-achievement", () => {
      // this.$bvModal.show("achievement-toast");
    });
  },

  created() {
    this.$Progress.start();
    this.$router.beforeEach((to, from, next) => {
      if (to.meta.progress !== undefined) {
        this.$Progress.parseMeta(to.meta.progress);
      }
      this.$Progress.start();
      next();
    });
    this.$router.afterEach(() => {
      this.$Progress.finish();
    });
  },

  methods: {
    createGoogleMaps() {
      return new Promise((resolve, reject) => {
        const base = "https://maps.googleapis.com/maps/api/js";
        const key = process.env.VUE_APP_GOOGLE_API_KEY;
        const libs = "places";
        const gmap = document.createElement("script");
        gmap.setAttribute("async", true);

        gmap.src = `${base}?key=${key}&libraries=${libs}&loading=async`;
        gmap.type = "text/javascript";
        gmap.onload = resolve;
        gmap.onerror = reject;

        document.body.appendChild(gmap);
      });
    },

    initTheme() {
      if (this.isDarkTheme) {
        document.body.classList.remove("light");
        document.body.classList.add("dark");
      } else {
        document.body.classList.remove("dark");
        document.body.classList.add("light");
      }
    },

    initAppUrls() {
      const externalApps = this.$lodash.mapValues(externals, (app) => app.domain);

      externalApps.bolt = window.location.origin;

      for (const key in externalApps) {
        if (!this.$ability.can("switcher", key)) {
          delete externalApps[key];
        }
      }

      this.appUrls = externalApps;
    },

    toggleTheme() {
      this.isDarkTheme = !this.isDarkTheme;
      localStorage.isDarkTheme = this.isDarkTheme ? "true" : "false";

      this.initTheme();
    },

    toggleSidebar() {
      if (document.body.classList.contains("sidebar-lg-show")) {
        document.body.classList.remove("sidebar-lg-show");
      } else {
        document.body.classList.add("sidebar-lg-show");
      }
    },
  },
};
</script>
